import {
	ConfirmationDialog,
	FlatIconButton,
	Icon,
	Id,
	SingleSelectField,
	TextButton,
	TextField,
	Trigger,
} from "@dgs/core";
import { FieldArray, useField } from "formik";
import { TFunction } from "i18next";
import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { isValidEmail } from "../../../admin/src/shared/utils/email";
import { ContactAnchor, ContactMeans } from "../../ui/ContactMeans";
import {
	ContactAttributeRequest,
	IContactAttributeBaseHandler,
	IContactAttributeHandler,
	IContactAttributeListHandler,
} from "../IContactAttributeHandler";
import { RelationListRow, RelationListSection, TwoColumn } from "./RelationListSection";

const typeOptions = ["private", "work", "group", "other"];

interface EmailValue {
	id: Id | null;
	email: string;
	type: "private" | "work" | "group" | "other" | null;
	isPrimary: boolean;
}

const emptyValue: EmailValue = { id: null, email: "", type: null, isPrimary: false };

const isEmpty = (value: EmailValue) => value.email === "" && value.type === null;

export function createEmailsContactAttributeBaseHandler(id: Id): IContactAttributeBaseHandler {
	return {
		type: "EMAILS",
		id,
		getLabel: (t) => t("Emails"),
	};
}

export function createEmailsContactAttributeProfileHandler(
	id: Id,
	value: EmailValue[],
): IContactAttributeHandler<EmailValue[]> {
	const baseHandler = createEmailsContactAttributeBaseHandler(id);
	return {
		...baseHandler,
		value,
		validate(t, values) {
			const errors: Record<number, any> = {};

			values.forEach((email, index) => {
				if (isEmpty(email)) return;

				if (!email.email) {
					errors[index] = { ...(errors[index] || {}), email: t("Field is required") };
				} else if (!isValidEmail(email.email)) {
					errors[index] = { ...(errors[index] || {}), email: t("Invalid email address") };
				} else if (
					values.some(
						(otherEmail, otherIndex) =>
							otherIndex !== index && otherEmail.email.toLowerCase() === email.email.toLowerCase(),
					)
				) {
					errors[index] = { ...(errors[index] || {}), email: t("The email already exists") };
				}
				if (!email.type) {
					errors[index] = { ...(errors[index] || {}), type: t("Field is required") };
				}
			});

			return errors;
		},
		renderValue(): ReactNode {
			if (value.length === 0) return "-";
			return (
				<ContactMeans
					items={value.map(({ email }) => email)}
					render={(email) => <ContactAnchor type="mailto">{email}</ContactAnchor>}
				/>
			);
		},
		renderFormField(t: TFunction, prefix): ReactNode {
			const name = `${prefix ? `${prefix}.` : ""}${id}.value`;
			return <FormField name={name} />;
		},
		toRequestValue(): ContactAttributeRequest<EmailValue[]> {
			return { id, value: this.value.filter((email) => !isEmpty(email)) };
		},
	};
}

export function createEmailsContactAttributeListHandler(id: Id): IContactAttributeListHandler {
	const baseHandler = createEmailsContactAttributeBaseHandler(id);
	return {
		...baseHandler,
		getColumnSize: () => "1fr",
	};
}

const FormField: FC<{ name: string }> = ({ name }) => {
	const { t } = useTranslation();
	const [{ value: emails }] = useField<EmailValue[]>(name);

	return (
		<FieldArray name={name}>
			{(props) => (
				<RelationListSection title={t("Email addresses")}>
					{emails.map((email, index) => (
						<RelationListRow key={index}>
							<TwoColumn>
								<TextField name={`${name}.${index}.email`} label={t("Name")} required readOnly={email.isPrimary} />
								<SingleSelectField
									name={`${name}.${index}.type`}
									label={t("Type")}
									options={typeOptions}
									getLabel={(option) => t(option)}
									getValue={String}
									clearable={false}
									required
								/>
							</TwoColumn>
							<Trigger
								render={(renderProps) => (
									<ConfirmationDialog
										heading={t("Delete")}
										labels={{
											confirm: t("Delete"),
											close: t("Cancel"),
										}}
										open={renderProps.open}
										onClose={renderProps.handleClose}
										action={(close) => {
											props.remove(index);
											close();
										}}
									>
										{t("Are you sure you want to delete this email address?")}
									</ConfirmationDialog>
								)}
							>
								{(triggerProps) => (
									<FlatIconButton
										{...triggerProps}
										type="button"
										title={t("Delete")}
										icon="delete"
										size="small"
										disabled={email.isPrimary}
									/>
								)}
							</Trigger>
						</RelationListRow>
					))}
					<span>
						<TextButton onClick={() => props.push(emptyValue)} type="button" prefix={<Icon $icon="plus" />}>
							{t("Add email address")}
						</TextButton>
					</span>
				</RelationListSection>
			)}
		</FieldArray>
	);
};
